import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
const SparePartsInventoryDashboard: React.FC = () => {
  const inventoryKPIs = [{
    title: 'Fill Rate %',
    value: '92.4%',
    change: '+2.1%',
    isPositive: true
  }, {
    title: 'Stock Ageing %',
    value: '18.5%',
    change: '-1.8%',
    isPositive: true
  }, {
    title: 'Obsolete Stock',
    value: '₹12.8 Cr',
    change: '+₹1.2 Cr',
    isPositive: false
  }];
  const ageingBucketsData = [{
    bucket: '0-3 Months',
    value: 145.2,
    percentage: 58.0,
    color: '#10b981'
  }, {
    bucket: '3-12 Months',
    value: 70.5,
    percentage: 28.2,
    color: '#f59e0b'
  }, {
    bucket: '12+ Months',
    value: 34.8,
    percentage: 13.8,
    color: '#ef4444'
  }];
  const movingPartsData = [{
    category: 'Fast Moving',
    count: 1245,
    value: 128.5,
    turnover: 8.2
  }, {
    category: 'Medium Moving',
    count: 892,
    value: 85.2,
    turnover: 4.5
  }, {
    category: 'Slow Moving',
    count: 623,
    value: 36.8,
    turnover: 1.8
  }];
  const regionAvailabilityData = [{
    region: 'North',
    available: 94.5,
    stockout: 5.5
  }, {
    region: 'West',
    available: 92.8,
    stockout: 7.2
  }, {
    region: 'South',
    available: 91.2,
    stockout: 8.8
  }, {
    region: 'East',
    available: 89.5,
    stockout: 10.5
  }];
  const skuInventoryData = [{
    sku: 'LED-HL-2024-M',
    description: 'LED Headlamp Module',
    category: 'Lighting',
    quantity: 2850,
    value: 14.25,
    age: 45,
    status: 'Fast Moving',
    turnover: 9.2,
    location: 'Delhi Warehouse'
  }, {
    sku: 'TAIL-LP-2023-T',
    description: 'Tail Lamp Assembly',
    category: 'Lighting',
    quantity: 2420,
    value: 12.1,
    age: 62,
    status: 'Fast Moving',
    turnover: 8.5,
    location: 'Mumbai Warehouse'
  }, {
    sku: 'DOOR-HDL-2024-M',
    description: 'Door Handle Assembly',
    category: 'Body Parts',
    quantity: 3185,
    value: 9.56,
    age: 38,
    status: 'Fast Moving',
    turnover: 7.8,
    location: 'Bangalore Warehouse'
  }, {
    sku: 'BRAK-PAD-2024-V',
    description: 'Brake Pad Assembly',
    category: 'Mechanical',
    quantity: 4250,
    value: 8.5,
    age: 28,
    status: 'Fast Moving',
    turnover: 10.2,
    location: 'Chennai Warehouse'
  }, {
    sku: 'WIRE-HRN-2023-K',
    description: 'Wiring Harness',
    category: 'Electrical',
    quantity: 1895,
    value: 11.37,
    age: 85,
    status: 'Medium Moving',
    turnover: 5.2,
    location: 'Pune Warehouse'
  }, {
    sku: 'INT-TRM-2024-H',
    description: 'Interior Trim Panel',
    category: 'Interior',
    quantity: 1650,
    value: 6.6,
    age: 118,
    status: 'Medium Moving',
    turnover: 4.8,
    location: 'Hyderabad Warehouse'
  }, {
    sku: 'HVAC-BLW-2023-T',
    description: 'HVAC Blower Motor',
    category: 'HVAC',
    quantity: 1285,
    value: 7.71,
    age: 142,
    status: 'Medium Moving',
    turnover: 3.9,
    location: 'Ahmedabad Warehouse'
  }, {
    sku: 'FUEL-PMP-2023-M',
    description: 'Fuel Pump Module',
    category: 'Fuel System',
    quantity: 985,
    value: 5.91,
    age: 168,
    status: 'Slow Moving',
    turnover: 2.5,
    location: 'Kolkata Warehouse'
  }, {
    sku: 'SENS-MOD-2022-K',
    description: 'Parking Sensor Module',
    category: 'Electronics',
    quantity: 685,
    value: 4.11,
    age: 285,
    status: 'Slow Moving',
    turnover: 1.8,
    location: 'Jaipur Warehouse'
  }, {
    sku: 'BATT-SYS-2021-H',
    description: 'Battery Management System',
    category: 'Electrical',
    quantity: 425,
    value: 6.38,
    age: 425,
    status: 'Obsolete',
    turnover: 0.5,
    location: 'Lucknow Warehouse'
  }, {
    sku: 'OLD-LAMP-2020-X',
    description: 'Legacy Lamp Assembly',
    category: 'Lighting',
    quantity: 285,
    value: 2.85,
    age: 520,
    status: 'Obsolete',
    turnover: 0.2,
    location: 'Chandigarh Warehouse'
  }, {
    sku: 'OLD-CTRL-2020-Y',
    description: 'Legacy Control Unit',
    category: 'Electronics',
    quantity: 185,
    value: 3.7,
    age: 580,
    status: 'Obsolete',
    turnover: 0.1,
    location: 'Indore Warehouse'
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Fast Moving':
        return 'text-green-600 bg-green-50 border-green-300';
      case 'Medium Moving':
        return 'text-yellow-600 bg-yellow-50 border-yellow-300';
      case 'Slow Moving':
        return 'text-orange-600 bg-orange-50 border-orange-300';
      case 'Obsolete':
        return 'text-red-600 bg-red-50 border-red-300';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-300';
    }
  };
  const getAgeColor = (age: number) => {
    if (age <= 90) return 'text-green-600 font-semibold';
    if (age <= 365) return 'text-yellow-600 font-semibold';
    return 'text-red-600 font-bold';
  };
  const getTurnoverColor = (turnover: number) => {
    if (turnover >= 7) return 'text-green-600 font-bold';
    if (turnover >= 4) return 'text-yellow-600 font-semibold';
    if (turnover >= 2) return 'text-orange-600 font-semibold';
    return 'text-red-600 font-bold';
  };
  return <div data-id="element-699">
      <DescriptionPanel title="Spare Parts Inventory Management" description="Comprehensive tracking of spare parts inventory including stock levels, ageing analysis, and availability across regions. Monitor fill rates, identify slow-moving items, and optimize inventory to reduce carrying costs while maintaining service levels." insights={['Fill rate at 92.4% - exceeding target of 90% with improved availability', '58% of inventory aged 0-3 months - healthy stock rotation', 'North region leading with 94.5% parts availability', 'Obsolete stock at ₹12.8 Cr - opportunity for clearance and cost reduction']} data-id="element-700" />
      {/* Inventory KPI Row */}
      <div className="mb-8" data-id="element-701">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-702">
          Inventory Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-703">
          {inventoryKPIs.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-704">
              <div className="flex flex-col h-full" data-id="element-705">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-706">
                  {kpi.title}
                </div>
                <div className="mt-3 text-3xl font-bold text-gray-900" data-id="element-707">
                  {kpi.value}
                </div>
                <div className={`mt-2 text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-708">
                  {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-709" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-710" />}
                  {kpi.change}
                </div>
              </div>
            </Card>)}
        </div>
      </div>
      {/* Ageing Buckets and Fast vs Slow Moving */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-711">
        <Card className="p-6" data-id="element-712">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-713">
            Stock Ageing Buckets
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-714">
            Inventory distribution by age
          </p>
          <ResponsiveContainer width="100%" height={300} data-id="element-715">
            <PieChart data-id="element-716">
              <Pie data={ageingBucketsData} cx="50%" cy="50%" labelLine={false} label={({
              bucket,
              percentage
            }) => `${bucket}: ${percentage}%`} outerRadius={100} fill="#8884d8" dataKey="percentage" data-id="element-717">
                {ageingBucketsData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-718" />)}
              </Pie>
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number, name: string, props: any) => [`₹${props.payload.value} Cr (${value}%)`, props.payload.bucket]} data-id="element-719" />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-720">
            <div className="space-y-2" data-id="element-721">
              {ageingBucketsData.map((item, index) => <div key={index} className="flex items-center justify-between text-sm" data-id="element-722">
                  <div className="flex items-center" data-id="element-723">
                    <div className="w-3 h-3 rounded mr-2" style={{
                  backgroundColor: item.color
                }} data-id="element-724" />
                    <span className="text-gray-700" data-id="element-725">{item.bucket}</span>
                  </div>
                  <span className="font-semibold text-gray-800" data-id="element-726">
                    ₹{item.value} Cr
                  </span>
                </div>)}
            </div>
          </div>
        </Card>
        <Card className="p-6" data-id="element-727">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-728">
            Fast vs Slow Moving Parts
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-729">
            Inventory classification by turnover rate
          </p>
          <ResponsiveContainer width="100%" height={300} data-id="element-730">
            <BarChart data={movingPartsData} data-id="element-731">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-732" />
              <XAxis dataKey="category" tick={{
              fontSize: 12
            }} data-id="element-733" />
              <YAxis yAxisId="left" tick={{
              fontSize: 12
            }} label={{
              value: 'Value (₹ Cr)',
              angle: -90,
              position: 'insideLeft'
            }} data-id="element-734" />
              <YAxis yAxisId="right" orientation="right" tick={{
              fontSize: 12
            }} label={{
              value: 'Turnover Rate',
              angle: 90,
              position: 'insideRight'
            }} data-id="element-735" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-736" />
              <Legend data-id="element-737" />
              <Bar yAxisId="left" dataKey="value" fill="#3b82f6" name="Value (₹ Cr)" data-id="element-738" />
              <Bar yAxisId="right" dataKey="turnover" fill="#10b981" name="Turnover Rate" data-id="element-739" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-740">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-741">
              <div className="text-center" data-id="element-742">
                <div className="text-gray-600" data-id="element-743">Fast Moving</div>
                <div className="text-xl font-bold text-green-600" data-id="element-744">1,245</div>
                <div className="text-xs text-gray-500" data-id="element-745">SKUs</div>
              </div>
              <div className="text-center" data-id="element-746">
                <div className="text-gray-600" data-id="element-747">Medium Moving</div>
                <div className="text-xl font-bold text-yellow-600" data-id="element-748">892</div>
                <div className="text-xs text-gray-500" data-id="element-749">SKUs</div>
              </div>
              <div className="text-center" data-id="element-750">
                <div className="text-gray-600" data-id="element-751">Slow Moving</div>
                <div className="text-xl font-bold text-orange-600" data-id="element-752">623</div>
                <div className="text-xs text-gray-500" data-id="element-753">SKUs</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Region-wise Spare Parts Availability */}
      <Card className="p-6 mb-8" data-id="element-754">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-755">
          Region-wise Spare Parts Availability
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-756">
          Parts availability and stockout rates by region
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-757">
          <BarChart data={regionAvailabilityData} layout="vertical" data-id="element-758">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-759" />
            <XAxis type="number" domain={[0, 100]} tick={{
            fontSize: 12
          }} data-id="element-760" />
            <YAxis dataKey="region" type="category" tick={{
            fontSize: 12
          }} data-id="element-761" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} formatter={(value: number) => `${value}%`} data-id="element-762" />
            <Legend data-id="element-763" />
            <Bar dataKey="available" stackId="a" fill="#10b981" name="Available %" data-id="element-764" />
            <Bar dataKey="stockout" stackId="a" fill="#ef4444" name="Stockout %" data-id="element-765" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-766">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-767">
            {regionAvailabilityData.map((region, index) => <div key={index} className="text-center" data-id="element-768">
                <div className="text-gray-600" data-id="element-769">{region.region}</div>
                <div className="text-xl font-bold text-green-600" data-id="element-770">
                  {region.available}%
                </div>
                <div className="text-xs text-red-600" data-id="element-771">
                  {region.stockout}% stockout
                </div>
              </div>)}
          </div>
        </div>
      </Card>
      {/* SKU Inventory Table */}
      <Card className="p-6" data-id="element-772">
        <div className="flex items-center justify-between mb-4" data-id="element-773">
          <div data-id="element-774">
            <h3 className="text-lg font-semibold text-gray-800" data-id="element-775">
              SKU Inventory Details
            </h3>
            <p className="text-sm text-gray-600" data-id="element-776">
              Detailed inventory breakdown by SKU with quantity, value, and
              ageing
            </p>
          </div>
          <div className="flex items-center space-x-2" data-id="element-777">
            <AlertTriangleIcon size={20} className="text-orange-600" data-id="element-778" />
            <span className="text-sm font-semibold text-orange-600" data-id="element-779">
              3 Obsolete Items
            </span>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-780">
          <table className="w-full" data-id="element-781">
            <thead data-id="element-782">
              <tr className="border-b-2 border-gray-300" data-id="element-783">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-784">
                  SKU Code
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-785">
                  Description
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-786">
                  Category
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-787">
                  Quantity
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-788">
                  Value
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-789">
                  Age (Days)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-790">
                  Turnover
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-791">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-792">
                  Location
                </th>
              </tr>
            </thead>
            <tbody data-id="element-793">
              {skuInventoryData.map((item, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-794">
                  <td className="py-3 px-4 text-sm font-mono text-blue-600" data-id="element-795">
                    {item.sku}
                  </td>
                  <td className="py-3 px-4 text-sm font-semibold text-gray-800" data-id="element-796">
                    {item.description}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-797">
                    {item.category}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-medium text-gray-900" data-id="element-798">
                    {item.quantity.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-bold text-gray-900" data-id="element-799">
                    ₹{item.value} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right" data-id="element-800">
                    <span className={getAgeColor(item.age)} data-id="element-801">{item.age}</span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right" data-id="element-802">
                    <span className={getTurnoverColor(item.turnover)} data-id="element-803">
                      {item.turnover}x
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-804">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(item.status)}`} data-id="element-805">
                      {item.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-806">
                    {item.location}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-807">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-808">
            <div className="text-center" data-id="element-809">
              <div className="text-gray-600" data-id="element-810">Total SKUs</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-811">12</div>
            </div>
            <div className="text-center" data-id="element-812">
              <div className="text-gray-600" data-id="element-813">Total Quantity</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-814">21,044</div>
            </div>
            <div className="text-center" data-id="element-815">
              <div className="text-gray-600" data-id="element-816">Total Value</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-817">₹93.04 Cr</div>
            </div>
            <div className="text-center" data-id="element-818">
              <div className="text-gray-600" data-id="element-819">Fast Moving</div>
              <div className="text-xl font-bold text-green-600" data-id="element-820">4</div>
            </div>
            <div className="text-center" data-id="element-821">
              <div className="text-gray-600" data-id="element-822">Slow Moving</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-823">2</div>
            </div>
            <div className="text-center" data-id="element-824">
              <div className="text-gray-600" data-id="element-825">Obsolete</div>
              <div className="text-xl font-bold text-red-600" data-id="element-826">3</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default SparePartsInventoryDashboard;